import { useEffect, useState } from "react";
import { useLocation } from "react-router";

// Map of route segments to their display names
const routeSegments: Record<string, string> = {
  dashboard: "Dashboard",
  orders: "Orders",
  create: "Create",
  edit: "Edit",
  account: "My Profile",
  "business-account": "Business Account",
  billing: "Billing",
  "payment-methods": "Payment Methods",
  "my-address-book": "Address Book",
  invoices: "Invoices",
  "privacy-policy": "Privacy Policy",
  "terms-of-use": "Terms of Use",
  onboarding: "Onboarding",
  "step-1": "Step 1",
  "step-2": "Step 2",
  success: "Complete",
  settings: "Settings",
  tracking: "Track Shipment",
  unauthorized: "Unauthorized",
};

// Map of exact routes to their corresponding titles
const exactRouteTitles: Record<string, string> = {
  "/": "Home | TooEZ",
};

// Function to extract route parameters
const extractRouteParams = (pathname: string): Record<string, string> => {
  const params: Record<string, string> = {};

  // Extract orderId from edit order route
  const orderEditMatch = /\/orders\/edit\/([^/]+)/.exec(pathname);
  if (orderEditMatch?.[1]) {
    params.orderId = orderEditMatch[1];
  }

  // Extract tracking number from tracking route
  const trackingMatch = /\/tracking\/([^/]+)/.exec(pathname);
  if (trackingMatch?.[1]) {
    params.trackingNumber = trackingMatch[1];
  }

  return params;
};

// Function to generate breadcrumb title
const generateBreadcrumbTitle = (
  pathname: string,
  params: Record<string, string>
): string => {
  // Handle home page
  if (pathname === "/") {
    return "Home | TooEZ";
  }

  // Split the path into segments
  const segments = pathname.split("/").filter(Boolean);

  // Map segments to their display names
  const breadcrumbs = segments.map((segment) => {
    // Check if this segment is a parameter (like an ID)
    if (segment === params.orderId && params.orderId) {
      return `#${params.orderId}`;
    }
    if (segment === params.trackingNumber && params.trackingNumber) {
      return `#${params.trackingNumber}`;
    }

    // Return the display name or the segment itself if not found
    return routeSegments[segment] || segment;
  });

  // Join the breadcrumbs with a separator and add the site name
  return `${breadcrumbs.join(" › ")} | TooEZ`;
};

// Interface for order data
interface OrderData {
  id: string;
  name?: string;
  reference?: string;
  status?: string;
}

// Interface for API response
interface OrderResponse {
  id: string;
  name?: string;
  reference?: string;
  status?: string;
}

export const DynamicTitle = () => {
  const location = useLocation();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to fetch order data
  const fetchOrderData = async (orderId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/orders/${orderId}`);
      if (response.ok) {
        const data = (await response.json()) as OrderResponse;
        setOrderData(data);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Get the current path
    const { pathname } = location;

    // Reset order data when route changes
    setOrderData(null);

    // Check for exact route match
    if (exactRouteTitles[pathname]) {
      document.title = exactRouteTitles[pathname];
      return;
    }

    // Extract route parameters
    const params = extractRouteParams(pathname);

    // Generate breadcrumb title
    document.title = generateBreadcrumbTitle(pathname, params);

    // Handle dynamic routes with parameters that need additional data
    if (pathname.startsWith("/orders/edit/") && params.orderId) {
      // Set loading state in title
      document.title = `Orders › Edit › Loading... | TooEZ`;
      // Fetch order data for a more descriptive title
      void fetchOrderData(params.orderId);
    }
  }, [location]);

  // Update title when order data is loaded
  useEffect(() => {
    if (
      orderData &&
      location.pathname.includes(`/orders/edit/${orderData.id}`) &&
      !isLoading
    ) {
      const orderName = orderData.name ?? orderData.reference ?? orderData.id;
      const orderStatus = orderData.status ? ` (${orderData.status})` : "";
      document.title = `Orders › Edit › ${orderName}${orderStatus} | TooEZ`;
    }
  }, [orderData, location, isLoading]);

  // This component doesn't render anything
  return null;
};

export default DynamicTitle;
